import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Save, Phone, Mail, MapPin, Clock, ExternalLink, Plus, Trash2, GripVertical } from 'lucide-react';

const ContactManagement = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    practice_name: '',
    phone: '',
    email: '',
    address: '',
    opening_hours: [],
    doctolib_link: '',
    google_maps_link: '',
    google_reviews_link: '',
    facebook_link: '',
    instagram_link: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchContactData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/contact`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Convert old format (object) to new format (array) if needed
        let openingHours = data.opening_hours || [];
        if (!Array.isArray(openingHours)) {
          // Convert object format to array format
          openingHours = Object.entries(openingHours).map(([key, value], index) => ({
            id: `${Date.now()}-${index}`,
            label: key.charAt(0).toUpperCase() + key.slice(1),
            value: value
          }));
        }
        setFormData({
          ...data,
          opening_hours: openingHours
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch contact data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchContactData();
  }, [fetchContactData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/admin/contact`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage('Kontaktdaten erfolgreich gespeichert!');
      } else {
        setMessage('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Failed to save contact data:', error);
      setMessage('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Opening Hours Management
  const addOpeningHour = () => {
    setFormData(prev => ({
      ...prev,
      opening_hours: [
        ...prev.opening_hours,
        {
          id: `${Date.now()}`,
          label: '',
          value: ''
        }
      ]
    }));
  };

  const updateOpeningHour = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      opening_hours: prev.opening_hours.map(hour =>
        hour.id === id ? { ...hour, [field]: value } : hour
      )
    }));
  };

  const removeOpeningHour = (id) => {
    setFormData(prev => ({
      ...prev,
      opening_hours: prev.opening_hours.filter(hour => hour.id !== id)
    }));
  };

  const moveOpeningHour = (index, direction) => {
    const newHours = [...formData.opening_hours];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newHours.length) return;
    
    [newHours[index], newHours[targetIndex]] = [newHours[targetIndex], newHours[index]];
    
    setFormData(prev => ({
      ...prev,
      opening_hours: newHours
    }));
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#1B4D3E' }}>Kontaktdaten</h1>
        <p className="text-gray-500">Praxisdaten und Öffnungszeiten verwalten</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#1B4D3E' }}>
              <Phone className="w-5 h-5" />
              Grunddaten
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Praxisname</label>
                <input
                  type="text"
                  value={formData.practice_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, practice_name: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Telefon</label>
                <input
                  type="text"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">E-Mail</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Adresse</label>
                <input
                  type="text"
                  value={formData.address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Opening Hours - Dynamic */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#1B4D3E' }}>
                <Clock className="w-5 h-5" />
                Öffnungszeiten
              </h2>
              <Button
                type="button"
                onClick={addOpeningHour}
                size="sm"
                style={{ backgroundColor: '#1B4D3E' }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Hinzufügen
              </Button>
            </div>

            <div className="space-y-3">
              {formData.opening_hours && formData.opening_hours.length > 0 ? (
                formData.opening_hours.map((hour, index) => (
                  <div key={hour.id} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    {/* Move buttons */}
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => moveOpeningHour(index, 'up')}
                        disabled={index === 0}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                        title="Nach oben"
                      >
                        <GripVertical className="w-3 h-3 rotate-180" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveOpeningHour(index, 'down')}
                        disabled={index === formData.opening_hours.length - 1}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                        title="Nach unten"
                      >
                        <GripVertical className="w-3 h-3" />
                      </button>
                    </div>
                    
                    {/* Label (e.g., "Mo", "Di-Do", etc.) */}
                    <input
                      type="text"
                      value={hour.label || ''}
                      onChange={(e) => updateOpeningHour(hour.id, 'label', e.target.value)}
                      className="w-24 px-3 py-2 border rounded-lg text-sm font-medium"
                      placeholder="Tag(e)"
                    />
                    
                    {/* Time/Value */}
                    <input
                      type="text"
                      value={hour.value || ''}
                      onChange={(e) => updateOpeningHour(hour.id, 'value', e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      placeholder="z.B. 08:30 - 12:00, 14:00 - 18:00 Uhr"
                    />
                    
                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={() => removeOpeningHour(hour.id)}
                      className="p-2 hover:bg-red-100 rounded-lg text-red-500"
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Keine Öffnungszeiten vorhanden</p>
                  <p className="text-sm">Klicken Sie auf "Hinzufügen" um Zeiten einzutragen</p>
                </div>
              )}
            </div>
            
            <p className="text-xs text-gray-500 mt-3">
              Tipp: Im "Tag(e)"-Feld können Sie z.B. "Mo", "Di-Do", "Fr" oder "Sa & So" eingeben.
            </p>
          </div>

          {/* Links */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 lg:col-span-2">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#1B4D3E' }}>
              <ExternalLink className="w-5 h-5" />
              Links & Social Media
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Doctolib Link</label>
                <input
                  type="url"
                  value={formData.doctolib_link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, doctolib_link: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Google Maps Link</label>
                <input
                  type="url"
                  value={formData.google_maps_link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, google_maps_link: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Google Reviews Link</label>
                <input
                  type="url"
                  value={formData.google_reviews_link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, google_reviews_link: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Facebook</label>
                <input
                  type="url"
                  value={formData.facebook_link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, facebook_link: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Instagram</label>
                <input
                  type="url"
                  value={formData.instagram_link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, instagram_link: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>

        {message && (
          <div className={`mt-4 p-4 rounded-lg ${message.includes('erfolgreich') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {message}
          </div>
        )}

        <div className="mt-6">
          <Button
            type="submit"
            disabled={saving}
            className="w-full md:w-auto"
            style={{ backgroundColor: '#1B4D3E' }}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Speichern...' : 'Änderungen speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactManagement;
